import {
  ContentSubmission,
  CONTENT_SUBMISSION_KIND,
} from '@/types/contentSubmission';

/**
 * Resolves the best URL to download for a content submission.
 *
 * Priority:
 *   1. submission.downloadUrl — explicit download URL (original MP4 for videos)
 *   2. latestVersion.url — preview/streaming URL fallback
 *   3. submission.url — legacy fallback
 *
 * Returns null when nothing is available (e.g. mixed-media parents).
 */
export function resolveDownloadUrl(
  submission: ContentSubmission
): string | null {
  if (submission.downloadUrl) {
    return submission.downloadUrl;
  }
  const latestVersionUrl =
    submission.versions?.find((v) => v.id === submission.latestVersionId)
      ?.url ?? null;
  return latestVersionUrl || submission.url || null;
}

/**
 * Builds a human-friendly filename: `{Creator Name} - {8-char id}[ - V{n}].{ext}`
 * Extension comes from the URL path; falls back to kind (photo→jpg, video→mp4).
 * versionNumber is appended only when provided (i.e. when downloading a specific
 * version and there are multiple versions).
 */
export function buildDownloadFileName(
  submission: ContentSubmission,
  url: string,
  versionNumber?: number
): string {
  const pathSegment = url.split('?')[0].split('/').pop() ?? '';
  const urlExtMatch = pathSegment.match(/\.([a-zA-Z0-9]{2,5})$/);
  const urlExt = urlExtMatch ? urlExtMatch[1].toLowerCase() : null;

  let kindExt: string | null = null;
  if (submission.kind === CONTENT_SUBMISSION_KIND.PHOTO) {
    kindExt = 'jpg';
  } else if (submission.kind === CONTENT_SUBMISSION_KIND.VIDEO) {
    kindExt = 'mp4';
  }

  const ext = urlExt || kindExt;

  const rawName = submission.profile?.displayName?.trim();
  const safeName =
    rawName
      ?.replace(/[\\/:*?"<>|]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || 'asset';

  const shortId = submission.id.slice(0, 8);
  const versionSuffix = versionNumber != null ? ` - V${versionNumber}` : '';
  const base = `${safeName} - ${shortId}${versionSuffix}`;
  return ext ? `${base}.${ext}` : base;
}
